import { Box, useTheme } from '@mui/material'
import React, { PropsWithChildren, ReactElement, useContext } from 'react'
import { FieldContext } from '../constants'

type TextEditorProps = {
  value: string
  onChange: (value: string) => void
  fieldError?: boolean
  fieldHelperText?: string
} & Record<string, unknown>

export const FieldTextEditor: React.FC<PropsWithChildren<TextEditorProps>> = ({
  value,
  onChange,
  fieldError,
  fieldHelperText,
  children,
  ...props
}) => {
  const theme = useTheme()
  const { isRequired, error, helperText, ...otherProps } = useContext(FieldContext)

  return (
    <Box
      sx={{
        border: fieldError
          ? `1px solid ${theme.colorToken.border.danger.normal}`
          : `1px solid ${theme.colorToken.border.neutral.bold}`,
        borderRadius: '6px',
        paddingY: '2px'
      }}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as ReactElement<any>, {
            value,
            onChange,
            isRequired,
            error: error || fieldError,
            helperText: helperText || fieldHelperText,
            options: {
              ...(child.props.options || {}),
              minimap: { enabled: false },
              formatOnPaste: true,
              formatOnType: true,
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              overviewRulerBorder: false,
              lineDecorationsWidth: 0,
              glyphMargin: false,
              renderLineHighlight: 'none',
              scrollbar: {
                verticalScrollbarSize: 6,
                horizontalScrollbarSize: 6
              },
              hideCursorInOverviewRuler: true,
              padding: { top: 8 },
              renderIndentGuides: false,
              guides: {
                indentation: false
              }
            },
            ...otherProps,
            ...props
          })
        }
        return child
      })}
    </Box>
  )
}
